import { useEffect, useRef } from 'react';

/**
 * useInlineEditDelegation — Squarespace-style SINGLE-click-to-edit for any
 * rendered heading/paragraph inside the website-builder canvas.
 *
 * Architecture (iter 123):
 *   • Hover affordance via data-wb-edit-hover (React preserves data-attrs).
 *   • Edit activation:
 *       - Walk up to outermost block-level text element (not inner spans).
 *       - Set contentEditable + data-wb-active-edit on it.
 *       - focus(), then on the next animation frame call
 *         document.execCommand('selectAll', false, null) — most reliable
 *         cross-browser way to select all text in a contenteditable AFTER
 *         the browser's default caret-placement has run.
 *   • Enter / Escape listeners are attached DIRECTLY to the editable element
 *     (one-shot, removed on blur). This avoids the iter 121/122 issue where
 *     a document-capture listener saw `active = null` because the effect
 *     closure was replaced by a React re-render between click and keypress.
 *   • CSS keys on `[data-wb-active-edit='1']` (distinct from `contenteditable`
 *     so we don't paint elements that are always-editable via <InlineText>).
 */
const EDITABLE_TAGS = new Set([
  'H1','H2','H3','H4','H5','H6','P','LI','TD','TH','BUTTON','A','SPAN','DIV','STRONG','EM','SMALL'
]);
const EDITABLE_TAG_SELECTOR = Array.from(EDITABLE_TAGS).join(',');
const BLOCK_LEVEL = new Set(['H1','H2','H3','H4','H5','H6','P','LI']);

function findFieldPath(obj, target, prefix = []) {
  if (obj == null) return null;
  if (typeof obj === 'string') {
    const v = obj.trim();
    if (!v) return null;
    if (v === target) return { path: prefix, score: 100 };
    if (target.length > 0 && v.length >= 2 && target.includes(v)) {
      return { path: prefix, score: Math.max(20, 80 - (target.length - v.length)) };
    }
    if (target.length >= 2 && v.includes(target)) {
      return { path: prefix, score: Math.max(10, 60 - (v.length - target.length)) };
    }
    return null;
  }
  let best = null;
  if (Array.isArray(obj)) {
    for (let i = 0; i < obj.length; i++) {
      const hit = findFieldPath(obj[i], target, [...prefix, i]);
      if (hit && (!best || hit.score > best.score)) best = hit;
    }
    return best;
  }
  if (typeof obj === 'object') {
    for (const [k, v] of Object.entries(obj)) {
      if (k.startsWith('_') && !['_extras_text', '_extras_text_blocks'].includes(k)) continue;
      const hit = findFieldPath(v, target, [...prefix, k]);
      if (hit && (!best || hit.score > best.score)) best = hit;
    }
  }
  return best;
}

function setByPath(obj, path, value) {
  if (!path.length) return value;
  const next = Array.isArray(obj) ? [...obj] : { ...(obj || {}) };
  const [head, ...rest] = path;
  next[head] = setByPath((obj || {})[head], rest, value);
  return next;
}

function getLeafText(el) {
  if (!el) return '';
  if (el.childNodes && el.childNodes.length === 1 && el.firstChild.nodeType === 3) {
    return (el.firstChild.textContent || '').trim();
  }
  return (el.textContent || '').trim();
}

/** Walk UP to the outermost block-level text element with the same trimmed
    textContent — avoids landing on inner spans inside an H1. */
function walkUpToOutermost(el) {
  let cur = el;
  let best = el;
  const target = getLeafText(el);
  for (let i = 0; i < 6; i++) {
    const parent = cur.parentElement;
    if (!parent) break;
    if (parent.getAttribute('data-testid')?.startsWith('live-block-')) break;
    if (!EDITABLE_TAGS.has(parent.tagName)) break;
    const parentText = (parent.textContent || '').trim();
    if (parentText !== target) break;
    best = parent;
    cur = parent;
  }
  // Prefer block-level (H1-H6/P/LI) over an inline span ancestor with same text
  if (!BLOCK_LEVEL.has(best.tagName) && best.parentElement && BLOCK_LEVEL.has(best.parentElement.tagName)) {
    const parentText = (best.parentElement.textContent || '').trim();
    if (parentText === target) return best.parentElement;
  }
  return best;
}

function resolveEditable(rawEl, blocksList) {
  if (!rawEl) return null;
  if (rawEl.closest('[data-no-inline-edit]')) return null;
  if (rawEl.closest('[data-wb-button="1"]')) return null;
  if (rawEl.closest('[data-wb-button-editor]')) return null;
  // Don't skip already-CE elements — InlineText emits contentEditable=true
  // unconditionally on the hero H1 / tagline P, and we DO want to overlay
  // our hover affordance + select-all + active-edit chrome on top of it.
  // InlineText keeps owning its own onBlur → persistence; our hook only
  // ADDS UX (visual outline, auto select-all, element-level Enter/Escape).
  const text = getLeafText(rawEl);
  if (!text || text.length > 600) return null;
  const wrapper = rawEl.closest('[data-testid^="live-block-"]');
  if (!wrapper) return null;
  const testid = wrapper.getAttribute('data-testid') || '';
  const blockId = testid.replace('live-block-', '');
  const block = (blocksList || []).find(b => b.id === blockId);
  if (!block) return null;
  const result = findFieldPath(block.content || {}, text);
  if (!result) return null;
  const el = walkUpToOutermost(rawEl);
  return { el, blockId, path: result.path, originalText: text, block };
}

export function useInlineEditDelegation({ canvasRef, blocks, onUpdateBlockContent, enabled = true }) {
  // Refs so handlers always see fresh state even when the effect closure was
  // captured by an older React render cycle.
  const blocksRef = useRef(blocks);
  useEffect(() => { blocksRef.current = blocks; }, [blocks]);
  const activeRef = useRef(null); // { el, blockId, originalText, onKey, onBlur }
  const lastHoveredRef = useRef(null);

  useEffect(() => {
    if (!enabled) return;

    const insideCanvas = (el) => {
      const canvas = canvasRef?.current;
      if (!canvas) return true;
      return canvas.contains(el);
    };

    const clearHover = () => {
      const el = lastHoveredRef.current;
      if (el) {
        try { el.removeAttribute('data-wb-edit-hover'); } catch {}
        lastHoveredRef.current = null;
      }
    };

    const teardownActive = () => {
      const a = activeRef.current;
      if (!a) return;
      try {
        a.el.removeAttribute('data-wb-active-edit');
        // Only clear contenteditable if WE set it (don't disable InlineText)
        if (a.weSetContentEditable) a.el.removeAttribute('contenteditable');
        a.el.removeEventListener('keydown', a.onKey, true);
        a.el.removeEventListener('blur', a.onBlur, true);
      } catch {}
      activeRef.current = null;
    };

    const commit = () => {
      const a = activeRef.current;
      if (!a) return;
      const next = (a.el.textContent || '').trim();
      const { blockId, originalText, weSetContentEditable } = a;
      teardownActive();
      // If InlineText owns the contentEditable (we didn't set it), let its
      // own onBlur handle persistence — avoid double-write conflicts.
      if (!weSetContentEditable) return;
      if (next && next !== originalText.trim()) {
        const currentBlock = (blocksRef.current || []).find(b => b.id === blockId);
        if (currentBlock) {
          const cur = findFieldPath(currentBlock.content || {}, originalText.trim());
          if (cur) {
            const curValue = cur.path.reduce((o, k) => (o == null ? o : o[k]), currentBlock.content);
            if (typeof curValue === 'string') {
              const replaced = curValue.replace(originalText.trim(), next);
              const newContent = setByPath(currentBlock.content || {}, cur.path, replaced);
              onUpdateBlockContent(blockId, newContent);
            }
          }
        }
      }
    };

    const onClick = (e) => {
      const raw = e.target.closest(EDITABLE_TAG_SELECTOR);
      if (!raw || !insideCanvas(raw)) return;

      // Iter 160 — skip elements that are managed by the React-state-driven
      // <InlineText> component. Those have `data-testid^="inline-"` and own
      // their own click-to-edit lifecycle (isEditing state + contentEditable
      // toggle). If we keep firing here we'd race with React, leave the DOM
      // contentEditable attribute out-of-sync with React state, and
      // stopPropagation would prevent the React onClick handler from ever
      // running.
      if (raw.matches && raw.matches('[data-testid^="inline-"]')) return;
      if (raw.closest && raw.closest('[data-testid^="inline-"]')) return;

      // Already in our active edit on this same element? Let the click pass
      // through so the browser places the caret normally.
      if (activeRef.current && activeRef.current.el === raw) return;
      if (activeRef.current && activeRef.current.el.contains(raw)) return;

      const info = resolveEditable(raw, blocksRef.current);
      if (!info) return;

      e.preventDefault();
      e.stopPropagation();

      // Commit any in-flight edit before starting a new one.
      if (activeRef.current) commit();

      const { el } = info;
      const alreadyCE = el.getAttribute('contenteditable') === 'true';
      if (!alreadyCE) {
        try { el.setAttribute('contenteditable', 'true'); } catch {}
      }
      try { el.setAttribute('data-wb-active-edit', '1'); } catch {}

      // Element-level listeners — attached HERE so we don't depend on document
      // -level closures that could see stale `active` after a React re-render.
      const onKey = (ev) => {
        if (ev.key === 'Enter' && !ev.shiftKey) {
          ev.preventDefault();
          try { el.blur(); } catch {}
          // blur handler will fire commit
        } else if (ev.key === 'Escape') {
          ev.preventDefault();
          try { el.textContent = info.originalText; el.blur(); } catch {}
        }
      };
      const onBlur = () => {
        // Defer slightly so focus may move into a related popover and back.
        setTimeout(() => {
          if (activeRef.current && activeRef.current.el === el && document.activeElement !== el) {
            commit();
          }
        }, 50);
      };
      el.addEventListener('keydown', onKey, true);
      el.addEventListener('blur', onBlur, true);

      activeRef.current = {
        el,
        blockId: info.blockId,
        originalText: info.originalText,
        path: info.path,
        weSetContentEditable: !alreadyCE,
        onKey,
        onBlur,
      };

      try { el.focus(); } catch {}

      // execCommand('selectAll') is the most reliable cross-browser way to
      // select all text in a contenteditable AFTER focus. Fall back to a
      // manual Range if execCommand is deprecated/no-op in the target browser.
      requestAnimationFrame(() => {
        if (!activeRef.current || activeRef.current.el !== el) return;
        try {
          if (document.activeElement !== el) el.focus();
          const ok = document.execCommand && document.execCommand('selectAll', false, null);
          if (!ok) {
            const range = document.createRange();
            range.selectNodeContents(el);
            const sel = window.getSelection();
            sel.removeAllRanges();
            sel.addRange(range);
          }
        } catch {}
      });
    };

    // Hover affordance — outline + 'Click to edit' badge via data attribute.
    const onMove = (e) => {
      const raw = e.target.closest(EDITABLE_TAG_SELECTOR);
      let el = null;
      if (raw && insideCanvas(raw)) {
        // Iter 160 — skip InlineText-managed elements (they own their own
        // hover/edit affordances via .wb-inline-text and React state).
        const isInline = raw.matches?.('[data-testid^="inline-"]') || raw.closest?.('[data-testid^="inline-"]');
        if (!isInline) {
          const info = resolveEditable(raw, blocksRef.current);
          if (info) el = info.el;
        }
      }
      if (el === lastHoveredRef.current) return;
      // Clear previous hover
      clearHover();
      if (!el) return;
      // Don't paint hover on the element currently being edited
      if (el.getAttribute('data-wb-active-edit') === '1') return;
      try { el.setAttribute('data-wb-edit-hover', '1'); } catch {}
      lastHoveredRef.current = el;
    };

    document.addEventListener('click', onClick, true);
    document.addEventListener('mousemove', onMove, true);

    return () => {
      document.removeEventListener('click', onClick, true);
      document.removeEventListener('mousemove', onMove, true);
      clearHover();
      teardownActive();
    };
  }, [canvasRef, enabled, onUpdateBlockContent]);
}
